import React, { useCallback, useEffect, useState } from 'react';
import { Compass, Crown, Clover, Building2, Mountain, Plane } from 'lucide-react';
import DublinTab from './DublinTab';
import GalwayTab from './GalwayTab';
import SingaporeTab from './SingaporeTab';
import LondonTab from './LondonTab';
import PlanScreen from './plan/PlanScreen';
import EventHub, { EVENT_SECTIONS, type EventSection } from './events/EventHub';
import { races, DEFAULT_RACE_ID } from './events/catalog';
import { guides } from './events/guides';
import type { Race } from './events/types';
import './index.css';

type Route =
  | { view: 'plan' }
  | { view: 'race'; raceId: string; section: EventSection }
  | { view: 'london' }
  | { view: 'dublin' }
  | { view: 'galway' }
  | { view: 'singapore' };

type PastTrip = 'london' | 'dublin' | 'galway' | 'singapore';

const SELECTED_RACE_KEY = 'selectedRaceId';

const readSelectedRace = (): string | null => {
  try {
    const id = localStorage.getItem(SELECTED_RACE_KEY);
    return id && races.some((r) => r.id === id) ? id : null;
  } catch {
    return null;
  }
};

const parseHash = (hash: string, selectedRaceId: string | null): Route => {
  if (hash.startsWith('#plan')) return { view: 'plan' };
  if (hash.startsWith('#london')) return { view: 'london' };
  if (hash.startsWith('#dublin')) return { view: 'dublin' };
  if (hash.startsWith('#galway')) return { view: 'galway' };
  if (hash.startsWith('#singapore')) return { view: 'singapore' };
  // Old links: #chicago pointed at the Chicago itinerary before the app went multi-race.
  if (hash.startsWith('#chicago')) return { view: 'race', raceId: 'chicago-2026', section: 'itinerary' };
  const match = /^#race\/([\w-]+)(?:\/([\w-]+))?/.exec(hash);
  if (match && races.some((r) => r.id === match[1])) {
    const section = EVENT_SECTIONS.find((s) => s === match[2]) ?? 'itinerary';
    return { view: 'race', raceId: match[1], section };
  }
  const fallback = selectedRaceId ?? DEFAULT_RACE_ID;
  return fallback ? { view: 'race', raceId: fallback, section: 'itinerary' } : { view: 'plan' };
};

const routeToHash = (route: Route): string => {
  if (route.view === 'race') return `#race/${route.raceId}${route.section === 'itinerary' ? '' : `/${route.section}`}`;
  return `#${route.view}`;
};

const titleFor = (route: Route, race?: Race): string => {
  if (route.view === 'london') return 'London Marathon 2026';
  if (route.view === 'dublin') return 'Dublin 2026';
  if (route.view === 'galway') return 'Galway 2026';
  if (route.view === 'singapore') return 'Singapore Layover — May 2026';
  if (route.view === 'race' && race) return `${race.name} ${race.date.slice(0, 4)} — Maraplan`;
  return 'Maraplan — plan your next big race';
};

function App() {
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(readSelectedRace);
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash, readSelectedRace()));
  const [londonResetTick, setLondonResetTick] = useState(0);

  const navigate = useCallback((next: Route) => {
    setRoute(next);
    const hash = routeToHash(next);
    // London manages its own #london/<section> suffix; don't clobber it on re-render.
    if (!(next.view === 'london' && window.location.hash.startsWith('#london')) && window.location.hash !== hash) {
      window.location.hash = hash;
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => setRoute(parseHash(window.location.hash, readSelectedRace()));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (window.location.hash === '' || window.location.hash.startsWith('#chicago')) {
      window.history.replaceState(null, '', routeToHash(route));
    }
  }, [route]);

  const race = route.view === 'race' ? races.find((r) => r.id === route.raceId) : undefined;
  const navRace = races.find((r) => r.id === (route.view === 'race' ? route.raceId : selectedRaceId ?? DEFAULT_RACE_ID));

  useEffect(() => {
    document.title = titleFor(route, race);
  }, [route, race]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [route.view]);

  const selectRace = (r: Race) => {
    setSelectedRaceId(r.id);
    try {
      localStorage.setItem(SELECTED_RACE_KEY, r.id);
    } catch {
      // Storage unavailable — selection lasts for this visit only.
    }
    navigate({ view: 'race', raceId: r.id, section: guides[r.id]?.itinerary ? 'itinerary' : 'fuelling' });
  };

  const navTheme = route.view === 'race' ? guides[route.raceId]?.theme ?? 'generic' : route.view;

  return (
    <div className="app">
      <nav className={`tab-nav tab-nav--${navTheme}`}>
        <button
          className={`tab-btn tab-btn--plan-tab ${route.view === 'plan' ? 'tab-btn--active' : ''}`}
          onClick={() => navigate({ view: 'plan' })}
        >
          <Compass size={18} />
          <span>Plan</span>
        </button>
        {navRace && (
          <button
            className={`tab-btn tab-btn--race-tab tab-btn--${guides[navRace.id]?.theme ?? 'generic'}-tab ${route.view === 'race' ? 'tab-btn--active' : ''}`}
            onClick={() =>
              navigate({ view: 'race', raceId: navRace.id, section: route.view === 'race' ? route.section : 'itinerary' })
            }
          >
            <Building2 size={18} />
            <span>{navRace.city}</span>
          </button>
        )}
        {route.view === 'london' && (
          <button
            className="tab-btn tab-btn--london-tab tab-btn--active"
            onClick={() => setLondonResetTick((t) => t + 1)}
          >
            <Crown size={18} />
            <span>London</span>
          </button>
        )}
        {route.view === 'dublin' && (
          <button className="tab-btn tab-btn--dublin-tab tab-btn--active">
            <Clover size={18} />
            <span>Dublin</span>
          </button>
        )}
        {route.view === 'galway' && (
          <button className="tab-btn tab-btn--galway-tab tab-btn--active">
            <Mountain size={18} />
            <span>Galway</span>
          </button>
        )}
        {route.view === 'singapore' && (
          <button className="tab-btn tab-btn--singapore-tab tab-btn--active">
            <Plane size={18} />
            <span>Singapore</span>
          </button>
        )}
      </nav>

      {route.view === 'plan' ? (
        <PlanScreen
          selectedRaceId={selectedRaceId}
          onSelectRace={selectRace}
          onOpenPastTrip={(trip: PastTrip) => navigate({ view: trip })}
        />
      ) : route.view === 'london' ? (
        <LondonTab resetTick={londonResetTick} />
      ) : route.view === 'dublin' ? (
        <DublinTab />
      ) : route.view === 'galway' ? (
        <GalwayTab />
      ) : route.view === 'singapore' ? (
        <SingaporeTab />
      ) : race ? (
        <EventHub
          key={race.id}
          race={race}
          guide={guides[race.id]}
          section={route.section}
          onSectionChange={(section) => navigate({ view: 'race', raceId: race.id, section })}
        />
      ) : null}
    </div>
  );
}

export default App;
